import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, effect } from '@angular/core';
import { QueryValidationResponse } from '@app/modules/identification/models/Rules/identification/rule-engine-service.model';
import { TableHints } from '@app/shared/models/table-hint.model';
import { CodeEditorService, CommonService, TableHintService } from '@app/shared/services';
import { CodeEditorOptions, LineToMark } from '@app/shared/utils/codemirror';
import { CodemirrorComponent } from '@ctrl/ngx-codemirror';
import { BaseComponent } from '@r1-development/rps-shared-ui';
import CodeMirror, { Position, TextMarker, Token } from 'codemirror';
import 'codemirror/addon/hint/show-hint';
import 'codemirror/addon/lint/lint';
import 'codemirror/addon/search/searchcursor';
import 'codemirror/lib/codemirror';
import { takeUntil } from 'rxjs';
import 'src/assets/codemirror/addon/hint/hql-hint';
import 'src/assets/codemirror/mode/hql/hql';

export const QUERY_CHANGE_VALIDATION_DELAY = 360;

@Component({
  selector: 'app-code-editor',
  templateUrl: './code-editor.component.html',
  styleUrls: ['./code-editor.component.scss'],
  standalone: false,
})
export class CodeEditorComponent extends BaseComponent implements OnInit, OnDestroy {
  @ViewChild('codeEditor') codeEditor: CodemirrorComponent;
  @Input() query = '';
  @Output() queryChange: EventEmitter<string> = new EventEmitter<string>();
  @Input() autoFocus = false;
  @Input() disabled = false;
  @Input() mode = 'spark';
  @Input() options: CodeEditorOptions;
  @Input() allowEmpty = false;
  @Input() editorHeight = '400px';
  @Input() markedLines: LineToMark[] = [];

  // this is used to enable/disable the line marking feature
  @Input() enableLineMarking = true;

  // this is used to enable/disable the line marking feature
  @Input() lineMarkingReadonly = false;

  error = false;
  validationMessage = '';
  showValidationMessage = false;

  onQueryChangeTimeOut = null;
  formattedQuery = '';

  // this effect will listen to the validations on the query and will pass on the validations for processing
  private signalEffect = effect(
    () => {
      const validations = this.codeEditorService.validations();
      if (validations) {
        this.handleQueryValidations(validations);
      }
    },
    { manualCleanup: false }
  );

  private markers: { gutterMarker: number; lineMarker: TextMarker }[] = [];
  private tableHints: TableHints;

  constructor(
    private readonly commonService: CommonService,
    private readonly tableHintService: TableHintService,
    private readonly codeEditorService: CodeEditorService
  ) {
    super();
  }

  get editor() {
    return this.codeEditor?.codeMirror;
  }

  ngOnInit(): void {
    this.init();
    this.handleEditorRefresh();
  }

  ngOnDestroy(): void {
    this.signalEffect.destroy();
  }

  onQueryChange(query: string) {
    this.queryChange.emit(query);
    this.clearErrors();
    this.validate(query);
    this.handleOnQueryChange();
  }

  onCodeMirrorComponentLoaded(e: any): void {
    this.codeEditor?.codeMirror.on('inputRead', (cm, changeObj) => {
      const inputText = changeObj.text[0];
      // pattern to check if the current input is a letter (a-z) or dot (.)
      const pattern = /^[a-zA-Z.]+$/;

      // pattern to check if the current token at current cursor position is a string
      const inputStringPattern = /'+/g;
      const currentToken = cm.getTokenAt(cm.getCursor());

      // Showing autocompletion hints when the input is either a letter (a-z) or dot (.)
      // And the current input is not a string
      if (pattern.test(inputText) && !inputStringPattern.test(currentToken?.string)) {
        CodeMirror.commands.autocomplete(cm);
      }
    });
  }

  private handleOnQueryChange(): void {
    if (this.editor) {
      clearTimeout(this.onQueryChangeTimeOut);
      this.onQueryChangeTimeOut = setTimeout(() => {
        this.clearAllMarks();
        this.clearAllGutterErrors();
      }, 750);
    }
  }

  private handleQueryValidations(validations: QueryValidationResponse) {
    if (this.enableLineMarking) {
      if (this.editor) {
        this.processValidations(validations);
      }
    }
  }

  private processValidations(validations: QueryValidationResponse): void {
    this.clearAllMarks();
    this.clearAllGutterErrors();
    if (validations?.syntaxErrors?.length > 0) {
      validations?.syntaxErrors.forEach((error) => {
        const errMessage = error.message.split('\n\n==')[0] ?? 'Syntax Error';
        this.highlightError(errMessage, error.line - 1);
      });
    }
  }

  private getOptions(mode: string): CodeEditorOptions {
    let tables: Record<string, string[]> = {};
    switch (mode) {
      case 'hql':
        tables = this.commonService.hintOptions;
        break;
      case 'spark':
        mode = 'text/x-sparksql';
        this.tableHints = this.tableHintService.get();
        tables = this.tableHints.hints;
        break;
    }

    return {
      lineNumbers: true,
      theme: 'idea',
      mode,
      gutters: ['CodeMirror-lint-markers', 'error'],
      foldGutter: true,
      hintOptions: {
        completeSingle: false,
        completeOnSingleClick: true,
        tables,
      },
      lint: true,
      lineWrapping: true,
      readOnly: this.disabled,
      extraKeys: {
        'Ctrl-Space': (editor) => {
          setTimeout(() => {
            CodeMirror.commands.autocomplete(editor);
          }, 100);
          throw CodeMirror.Pass;
        },
      },
    };
  }

  private init() {
    this.changeMode(this.mode);
    this.clearErrors();
    this.validate(this.query);
  }

  private clearMarkers() {
    if (this.markers.length > 0) {
      this.markers.forEach((marker) => {
        marker.lineMarker.clear();
      });
      this.editor.clearGutter('error');
      this.markers = [];
    }
  }

  private clearErrors() {
    if (this.error) {
      this.clearMarkers();
      this.error = false;
      this.validationMessage = '';
    }
  }

  private changeMode(mode: string) {
    this.options = this.getOptions(mode);
  }

  private validate(query: string) {
    switch (this.mode) {
      case 'spark':
        this.sparkValidate();
        break;
    }
  }

  private isQueryEmpty() {
    if (!this.query || this.query.trim() === '') {
      if (!this.allowEmpty) {
        this.error = true;
        this.validationMessage = 'Rule cannot be empty';
      }

      return true;
    }

    return false;
  }

  private sparkValidate() {
    this.isQueryEmpty();
  }

  private highlightError(errorMessage: string, lineNumber: number = null) {
    if (lineNumber === null || lineNumber < 0) {
      const lastIndex = this.editor.getValue().split('\n').length - 1;
      const numberOfCharacters = this.editor.getValue().split('\n')[lastIndex].length;
      const lineMarker = this.editor.markText(
        { ch: 0, line: 0 },
        { ch: numberOfCharacters, line: lastIndex },
        {
          className: 'CodeMirror-lint-mark CodeMirror-lint-mark-error',
          title: errorMessage,
        }
      );

      this.markers.push({ gutterMarker: 0, lineMarker });
    } else {
      const lastIndex = this.editor.getValue().split('\n').length - 1;
      const count = this.editor.getValue().split('\n')[lineNumber > lastIndex ? lastIndex : lineNumber].length;
      const lineMarker = this.editor.markText(
        { ch: 0, line: lineNumber },
        { ch: count, line: lineNumber },
        {
          className: 'CodeMirror-lint-mark CodeMirror-lint-mark-error',
          title: errorMessage,
        }
      );

      this.markers.push({ gutterMarker: lineNumber, lineMarker });
    }
  }

  private makeMarker() {
    const marker = document.createElement('i');
    marker.style.color = '#F00000';
    marker.style.marginLeft = '-5px';
    marker.className = 'pi pi-exclamation-triangle';
    return marker;
  }

  private handleBetweenDblBracketPosition(currentWord: Token, cursorPosition: Position): Token {
    if (currentWord.string === '{') {
      cursorPosition.ch = cursorPosition.ch + 2;
      currentWord = this.editor.getTokenAt(cursorPosition);
      cursorPosition.ch = cursorPosition.ch - 2;
    }
    if (currentWord.string === '}') {
      cursorPosition.ch = cursorPosition.ch - 1;
      currentWord = this.editor.getTokenAt(cursorPosition);
      cursorPosition.ch = cursorPosition.ch + 1;
    }
    return currentWord;
  }

  private clearAllMarks(): void {
    this.editor.getAllMarks().forEach((x) => x.clear());
  }

  private clearAllGutterErrors(): void {
    this.editor.clearGutter('error');
  }

  private handleEditorRefresh(): void {
    this.codeEditorService
      .isRefreshEditor()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val: { refresh: boolean }) => {
        if (val.refresh) {
          setTimeout(() => {
            this.editor.refresh();
          }, 600);
        }
      });
  }
}
